import type { ApiErrorBody, ApiResponse } from '../types/api';
import { clearSession, getKioskSession, getToken } from '../stores/session';

export class ApiError extends Error {
  readonly code: number;
  readonly httpStatus: number;
  readonly body?: ApiErrorBody;

  constructor(message: string, code: number, httpStatus: number, body?: ApiErrorBody) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.httpStatus = httpStatus;
    this.body = body;
  }
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export function networkFailMessage(): string {
  return '当前无法连接校园网';
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit & { raw?: boolean } = {},
): Promise<T> {
  if (isOffline()) {
    throw new ApiError(networkFailMessage(), 0, 0);
  }
  const headers = new Headers(init.headers);
  const token = getToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const kiosk = getKioskSession();
  if (kiosk && !headers.has('X-Kiosk-Session')) {
    headers.set('X-Kiosk-Session', kiosk);
  }
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, { ...init, headers, cache: 'no-store' });
  } catch {
    throw new ApiError(networkFailMessage(), 0, 0);
  }
  if (init.raw) {
    if (!res.ok) {
      const json = (await res.json().catch(() => null)) as ApiErrorBody | null;
      throw toApiError(res.status, json);
    }
    return res as unknown as T;
  }
  const json = (await res.json().catch(() => null)) as ApiResponse<T> | ApiErrorBody | null;
  if (!json || typeof json !== 'object' || !('code' in json)) {
    throw new ApiError('响应无法解析', 50000, res.status);
  }
  if (!res.ok || json.code !== 0) {
    throw toApiError(res.status, json as ApiErrorBody);
  }
  return (json as ApiResponse<T>).data;
}

function toApiError(httpStatus: number, json?: ApiErrorBody | null): ApiError {
  const code = json?.code ?? httpStatus;
  if (httpStatus === 401 || code === 40101) {
    clearSession();
  }
  // 首次登录未改密时所有业务接口都 40301，直接说清原因，别让人以为是权限配错。
  if (code === 40301 && json?.data?.reason === 'MUST_CHANGE_PASSWORD') {
    return new ApiError('请先修改初始密码后再使用', code, httpStatus, json);
  }
  const message = mapErrorMessage(code, json?.message ?? '请求失败');
  return new ApiError(message, code, httpStatus, json ?? undefined);
}

export function mapErrorMessage(code: number, fallback: string): string {
  switch (code) {
    case 40001:
      return fallback || '请求不合法';
    case 40101:
      return '登录已失效，请重新登录';
    case 40301:
      return '没有权限执行该操作，请重新登录或联系实验员';
    case 40401:
      return '记录不存在';
    case 40909:
      return '该时段已占用';
    case 40910:
      return '名额已满';
    case 40911:
      return '已经报名或候补，请勿重复提交';
    case 40912:
      return fallback || '当前状态不允许该操作';
    case 40913:
      return '同一提交令牌已用于不同内容，请刷新后重试';
    case 40914:
      return '相同请求正在处理中';
    case 40915:
      return '房间编码已存在';
    case 42901:
      return '导出过于频繁';
    case 42902:
      return '尝试过多，请稍后';
    default:
      return fallback;
  }
}

export function fieldError(err: unknown, field: string): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  if (err.code === 40915 && field === 'room_code') return '房间编码已存在';
  const fields = err.body?.data?.fields;
  if (fields?.includes(field)) return err.message;
  return undefined;
}
